/**
 * Appointment length resolution — `tasks/appointment-type-duration.md`.
 *
 * Two sources can say how long an appointment is:
 *
 * - `Schedule.timeSlotDuration` — the practice-wide default, a STRING of minutes (`'30'`)
 *   because that is how the column was declared. It is also the calendar's GRID interval, which
 *   this helper has nothing to do with.
 * - `AppointmentType.durationMinutes` — an optional INTEGER override per type. `null` means "use
 *   the schedule default".
 *
 * Everything that derives an end time from a start time goes through `effectiveDurationMinutes`
 * so the precedence lives in exactly one place and both sources come out as one number.
 */

/** The picker list. Shared by the schedule's Duration select and the appointment type dialog so the two cannot drift. */
export const APPOINTMENT_DURATION_OPTIONS: ReadonlyArray<number> = [5, 10, 15, 20, 30, 45, 60, 90, 120, 180, 240];

/** What the schedule falls back to when it has no `timeSlotDuration` at all. Matches the settings screen's seed. */
export const FALLBACK_DURATION_MINUTES = 30;

/**
 * A positive whole number of minutes, or null. Accepts the schedule's string form and the
 * type's integer form alike; rejects `''`, `NaN`, zero and negatives rather than letting them
 * become a zero-length or backwards appointment.
 */
export function parseDurationMinutes(raw: unknown): number | null {
  if (raw === null || raw === undefined || raw === '') return null;
  const value = Number(raw);
  return Number.isInteger(value) && value > 0 ? value : null;
}

/**
 * The length, in minutes, of an appointment of `appointmentType` at a practice whose schedule is
 * `schedule`. Type override, else schedule default, else 30.
 *
 * Both arguments are optional on purpose: the booking form derives an end time before a type is
 * chosen (arriving from the calendar with only a start), and the calendar derives one for legacy
 * rows whose `typeDetails` did not load.
 */
export function effectiveDurationMinutes(
  appointmentType: { durationMinutes?: unknown } | null | undefined,
  schedule: { timeSlotDuration?: unknown } | null | undefined,
): number {
  return parseDurationMinutes(appointmentType?.durationMinutes)
    ?? parseDurationMinutes(schedule?.timeSlotDuration)
    ?? FALLBACK_DURATION_MINUTES;
}

/** `HH:mm` plus `minutes`, wrapping past midnight the way the rest of the calendar does. */
export function addMinutesToTime(time: string, minutes: number): string {
  const [h, m] = time.split(':').map(Number);
  const total = (((h * 60 + m + minutes) % 1440) + 1440) % 1440;
  const hh = String(Math.floor(total / 60)).padStart(2, '0');
  const mm = String(total % 60).padStart(2, '0');
  return `${hh}:${mm}`;
}
